import { useState } from 'react';
import {
  top10Categories,
  top10TypeConfig,
  top10Reglas,
  top10GlobalStats,
  top10Radios,
  top10Programas,
  top10Locutores,
  top10Clubes,
  top10Fans,
  top10Eventos,
  top10Concursos,
  getTop10EnVivo,
  getTop10TopRising,
  formatearNumero,
  type Top10EntityType,
  type Top10Entry,
} from '@/mocks/top10Global';

const movementConfig: Record<string, { icon: string; color: string; bg: string }> = {
  up: { icon: 'ri-arrow-up-line', color: 'text-emerald-500', bg: 'bg-emerald-50' },
  down: { icon: 'ri-arrow-down-line', color: 'text-red-500', bg: 'bg-red-50' },
  stable: { icon: 'ri-subtract-line', color: 'text-foreground-400', bg: 'bg-foreground-100' },
};

const trendDots: Record<string, string> = {
  exploding: 'bg-rose-500',
  trending: 'bg-amber-500',
  growing: 'bg-emerald-500',
  active: 'bg-sky-500',
  stable: 'bg-foreground-400',
  declining: 'bg-red-500',
};

function MovementBadge({ movement, value }: { movement: string; value: number }) {
  const cfg = movementConfig[movement];
  return (
    <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ${cfg.bg} text-xs font-semibold ${cfg.color}`}>
      <i className={`${cfg.icon} text-[10px]`}></i>
      {value}
    </div>
  );
}

function Top10Row({ entry, index }: { entry: Top10Entry; index: number }) {
  const movement = movementConfig[entry.movement];
  const dotColor = trendDots[entry.trend];

  return (
    <div className="flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/60 transition-all cursor-pointer group">
      {/* position */}
      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
        index < 3 ? (index === 0 ? 'bg-amber-100 text-amber-700' : index === 1 ? 'bg-slate-100 text-slate-600' : 'bg-orange-100 text-orange-700') : 'bg-background-100 text-foreground-600'
      }`}>
        <span className="text-sm font-black">#{entry.position}</span>
      </div>

      {/* movement */}
      <div className={`flex-shrink-0 w-7 h-7 rounded-lg ${movement.bg} flex items-center justify-center`}>
        <i className={`${movement.icon} ${movement.color} text-xs`}></i>
      </div>

      {/* logo */}
      <img src={entry.logoUrl} alt={entry.entityName} className="w-10 h-10 md:w-12 md:h-12 rounded-xl object-cover flex-shrink-0" loading="lazy" />

      {/* info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-bold text-foreground-900 truncate">{entry.entityName}</p>
          {entry.isLive && (
            <span className="flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-bold">
              <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse"></span>
              EN VIVO
            </span>
          )}
        </div>
        <p className="text-xs text-foreground-500 truncate mt-0.5">{entry.entitySubtitle}</p>
        <p className="text-[10px] text-foreground-400 mt-0.5">{entry.highlight} · {formatearNumero(entry.followers)} seguidores</p>
      </div>

      {/* score */}
      <div className="flex-shrink-0 text-right">
        <p className="text-sm font-bold text-foreground-900">{entry.scoreLabel}</p>
        <div className="flex items-center justify-end gap-2 mt-0.5">
          <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`}></span>
          <span className={`text-xs font-semibold ${entry.scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {entry.scoreChange >= 0 ? '+' : ''}{entry.scoreChange}
          </span>
          <span className="text-xs text-foreground-400">{entry.movementValue} pos</span>
        </div>
      </div>
    </div>
  );
}

type VistaModo = 'por-categoria' | 'en-vivo' | 'mayor-movimiento' | 'top-rising' | 'reglas';

export default function Top10GlobalPage() {
  const [activeType, setActiveType] = useState<Top10EntityType>('radios');
  const [viewMode, setViewMode] = useState<VistaModo>('por-categoria');

  const currentEntries = top10Categories[activeType];
  const typeInfo = top10TypeConfig[activeType];
  const enVivoEntries = getTop10EnVivo();
  const topRisingEntries = getTop10TopRising();

  return (
    <div className="min-h-screen bg-background-50">
      {/* hero */}
      <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Grand%20stadium%20arena%20with%20dramatic%20golden%20spotlight%20on%20central%20podium%2C%20championship%20atmosphere%20with%20falling%20confetti%2C%20seven%20illuminated%20pillars%20representing%20different%20competition%20categories%2C%20warm%20amber%20and%20gold%20color%20scheme%20dominantly%2C%20epic%20sports%20hall%20of%20fame%20aesthetic%2C%20cinematic%20wide%20angle%20shot%20with%20dramatic%20lighting%20and%20atmospheric%20haze%2C%20no%20text%20or%20logos%2C%20abstract%20competitive%20arena%20concept%20photography&width=1600&height=900&seq=top10-global-hero-2026&orientation=landscape"
          alt="Top 10 Global"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4 w-full max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/30 backdrop-blur-sm border border-amber-400/30 mb-4">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
              <span className="text-xs font-bold text-amber-200 whitespace-nowrap">ACTUALIZACIÓN AUTOMÁTICA</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-white mb-4 drop-shadow-lg">
              Top 10 Global
            </h1>
            <p className="text-sm md:text-base text-white/80 max-w-2xl mx-auto">
              El ranking que mide competencia real en 7 categorías. Posición, movimiento, score y tendencia — todo visible, todo automático, todo auditable.
            </p>

            {/* hero stats overlay */}
            <div className="inline-flex flex-wrap items-center justify-center gap-4 md:gap-8 mt-6 bg-white/10 backdrop-blur-md rounded-2xl px-6 py-4 border border-white/10">
              <div className="text-center">
                <p className="text-xl font-bold text-white">{top10GlobalStats.totalEntidadesRankeadas}</p>
                <p className="text-[10px] text-white/60">Entidades</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-white">7</p>
                <p className="text-[10px] text-white/60">Categorías</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-white">{top10GlobalStats.actualizacionesPorHora}</p>
                <p className="text-[10px] text-white/60">Update/h</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-white">{top10GlobalStats.entidadesEnVivoAhora}</p>
                <p className="text-[10px] text-white/60">En Vivo</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* sticky bar */}
      <div className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-xl border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            {/* back + title */}
            <div className="flex items-center gap-3">
              <a
                href="/"
                className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 hover:text-foreground-700 transition-colors"
              >
                <i className="ri-arrow-left-line text-sm"></i>
              </a>
              <div className="flex items-center gap-2">
                <i className="ri-trophy-line text-amber-500"></i>
                <span className="text-sm font-bold text-foreground-900">Top 10 Global</span>
              </div>
            </div>

            {/* view mode pills */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {([
                { id: 'por-categoria' as VistaModo, label: 'Por Categoría', icon: 'ri-stack-line' },
                { id: 'en-vivo' as VistaModo, label: 'En Vivo', icon: 'ri-live-line' },
                { id: 'top-rising' as VistaModo, label: 'Top Rising', icon: 'ri-rocket-line' },
                { id: 'reglas' as VistaModo, label: 'Reglas', icon: 'ri-scales-line' },
              ] as const).map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => setViewMode(mode.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    viewMode === mode.id
                      ? 'bg-amber-50 text-amber-700 border border-amber-200'
                      : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <i className={`${mode.icon} text-[11px]`}></i>
                  {mode.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* main content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Por Categoría view */}
        {viewMode === 'por-categoria' && (
          <>
            {/* type tabs */}
            <div className="flex flex-wrap items-center gap-1.5 mb-6">
              {(Object.keys(top10Categories) as Top10EntityType[]).map((type) => {
                const info = top10TypeConfig[type];
                const count = top10Categories[type].length;
                return (
                  <button
                    key={type}
                    onClick={() => setActiveType(type)}
                    className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      activeType === type
                        ? `${info.bg} ${info.color} rounded-full`
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    <i className={`${info.icon} text-sm`}></i>
                    {info.label}
                    <span className="text-xs opacity-60">({count})</span>
                  </button>
                );
              })}
            </div>

            {/* type description */}
            <div className="mb-6 p-4 rounded-xl bg-background-100 border border-background-200/70">
              <p className="text-sm text-foreground-600">{typeInfo.description}</p>
            </div>

            {/* podium */}
            {currentEntries.length >= 3 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {[1, 0, 2].map((idx) => {
                  const entry = currentEntries[idx];
                  const movement = movementConfig[entry.movement];
                  const placements = [
                    { label: 'ORO', color: 'from-amber-400 to-yellow-500', textColor: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
                    { label: 'PLATA', color: 'from-slate-400 to-slate-500', textColor: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
                    { label: 'BRONCE', color: 'from-orange-400 to-orange-600', textColor: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
                  ];
                  const placement = placements[idx];

                  return (
                    <div key={entry.entityName} className={`relative p-5 rounded-2xl border ${placement.border} ${placement.bg} cursor-pointer hover:shadow-sm transition-all`}>
                      <div className={`absolute top-3 right-3 px-2 py-0.5 rounded-md bg-gradient-to-r ${placement.color} text-white text-[10px] font-bold`}>
                        {placement.label}
                      </div>

                      <div className="flex items-center gap-3 mb-4">
                        <img src={entry.logoUrl} alt="" className="w-14 h-14 rounded-xl object-cover ring-2 ring-white/60" loading="lazy" />
                        <div className="min-w-0">
                          <p className="text-base font-bold text-foreground-900 truncate">{entry.entityName}</p>
                          <p className="text-xs text-foreground-500 truncate">{entry.entitySubtitle}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xl font-black text-foreground-800">#{entry.position}</span>
                        <MovementBadge movement={entry.movement} value={entry.movementValue} />
                        <span className={`text-sm font-bold ${typeInfo.color}`}>{entry.scoreLabel}</span>
                        <span className={`text-xs font-semibold ${entry.scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {entry.scoreChange >= 0 ? '+' : ''}{entry.scoreChange}
                        </span>
                        {entry.isLive && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-bold">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                            EN VIVO
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-foreground-500 mt-3">{entry.highlight} · {formatearNumero(entry.followers)} seguidores</p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* rest of top 10 */}
            <div className="space-y-2">
              {currentEntries.slice(3, 10).map((entry, i) => (
                <Top10Row key={entry.entityName} entry={entry} index={i + 3} />
              ))}
            </div>
          </>
        )}

        {/* En Vivo view */}
        {viewMode === 'en-vivo' && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-foreground-900 mb-1 flex items-center gap-2">
                <i className="ri-live-line text-red-500"></i>
                Top 10 — En Vivo Ahora
              </h2>
              <p className="text-sm text-foreground-600">Las 10 entidades mejor rankeadas que están transmitiendo en este momento.</p>
            </div>
            <div className="space-y-2">
              {enVivoEntries.map((entry, i) => (
                <Top10Row key={entry.entityName} entry={entry} index={i} />
              ))}
            </div>
            {enVivoEntries.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-live-line text-foreground-300 text-2xl"></i>
                </div>
                <p className="text-sm text-foreground-500">No hay entidades en vivo en este momento</p>
              </div>
            )}
          </>
        )}

        {/* Top Rising view */}
        {viewMode === 'top-rising' && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-foreground-900 mb-1 flex items-center gap-2">
                <i className="ri-rocket-line text-rose-500"></i>
                Top 10 — Mayor Crecimiento
              </h2>
              <p className="text-sm text-foreground-600">Las 10 entidades con mayor aumento de score en la última actualización.</p>
            </div>
            <div className="space-y-2">
              {topRisingEntries.map((entry, i) => (
                <Top10Row key={entry.entityName} entry={entry} index={i} />
              ))}
            </div>
          </>
        )}

        {/* Reglas view */}
        {viewMode === 'reglas' && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold text-foreground-900 mb-1 flex items-center gap-2">
                <i className="ri-scales-line text-violet-500"></i>
                Reglas del Sistema
              </h2>
              <p className="text-sm text-foreground-600">Las 4 reglas que gobiernan el Top 10 Global. Diseñadas para competencia justa y transparente.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              {top10Reglas.map((regla) => (
                <div key={regla.id} className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${regla.gradiente} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${regla.icono} text-white text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-foreground-900 mb-2">{regla.titulo}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{regla.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* stats summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-5 rounded-2xl bg-background-100 border border-background-200/70">
              <div className="text-center">
                <p className="text-lg font-bold text-foreground-900">{top10GlobalStats.totalEntidadesRankeadas}</p>
                <p className="text-xs text-foreground-500">Entidades</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-foreground-900">7</p>
                <p className="text-xs text-foreground-500">Categorías</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-foreground-900">{top10GlobalStats.actualizacionesPorHora}/h</p>
                <p className="text-xs text-foreground-500">Actualizaciones</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-foreground-900">{top10GlobalStats.scorePromedioTop10}</p>
                <p className="text-xs text-foreground-500">Score Promedio</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-foreground-900">{top10GlobalStats.movimientoPromedio}</p>
                <p className="text-xs text-foreground-500">Mov. Promedio</p>
              </div>
            </div>
          </>
        )}

        {/* CTA */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="/rankings"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-background-100 border border-background-200/70 text-foreground-700 text-sm font-bold hover:bg-background-200/50 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-bar-chart-line"></i>
            Explorar Rankings Completos
          </a>
          <a
            href="/tv-app"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold hover:from-amber-600 hover:to-orange-600 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-tv-line"></i>
            Ver en TV App
          </a>
        </div>
      </div>
    </div>
  );
}